import {
  SidebarDivider, SidebarMenuItem, SidebarTopText,
  SidebarTopTextBold,
  Tooltip, checkTruncation, useIsMsalAuth,
} from '@equifinance/shared';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { useAccountContext } from '../../../contexts';
import { UnderwritingStatuses } from '../../../contexts/applications';

const UserNameDiv = styled.div`${({ theme }) => `
  width: 145px;
  text-overflow: ellipsis;
  overflow: hidden;
  padding-left: ${theme.padding[1]};
`}`;

export const UserIcon = styled.i.attrs(() => ({
  className: 'mi-user',
}))` ${({ theme }) => `
  font-size:${theme.fontSize[2]};
  padding-right:${theme.padding[2]};
`}`;

export default function ProductSidebarHeader() {
  const { account, id: accountId, creditSearchCompleted } = useAccountContext();
  const isMsalAuth = useIsMsalAuth();
  const textRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isTruncated, setIsTruncated] = useState<{ [key: number]: boolean }>({});

  const { applicants, securityAddress } = account ?? {};
  const { postCode } = securityAddress ?? {};

  const applicationStage = useMemo(() => {
    if (['WithBroker', 'WithLender'].includes(account.status)) {
      return UnderwritingStatuses[account.stage] || '';
    }
    return account.status || '';
  }, [account?.stage, account?.status]);

  useEffect(() => {
    /**
     * Function to check if the text content of each element in `textRefs` is truncated.
     * It updates the `isTruncated` state with a boolean value for each element.
     */
    const updateTruncationState = () => {
      const newTruncatedState = checkTruncation(textRefs.current);
      setIsTruncated(newTruncatedState);
    };

    // Call the truncation check function initially and whenever `applicants` or `accountId` changes.
    updateTruncationState();
  }, [applicants, accountId, account?.brokerageName]);

  return (
    <>
      { account && (
      <>
        {(isMsalAuth || creditSearchCompleted) && (
          <SidebarMenuItem>
            <SidebarTopText>
              {`Application no.: ${account?.applicationNumber ?? ''}`}
            </SidebarTopText>
          </SidebarMenuItem>
        )}

        <SidebarMenuItem>
          <SidebarTopText>
            {`Stage: ${applicationStage}`}
          </SidebarTopText>
        </SidebarMenuItem>

        <SidebarMenuItem>
          <SidebarTopText>
            {`Security postcode: ${postCode ?? '-'}`}
          </SidebarTopText>
        </SidebarMenuItem>

        {applicants?.map(({ id, personalDetails }, index) => (
          <>
            <SidebarMenuItem>
              <SidebarTopText key={`${personalDetails?.id}-${id}`} id={`${personalDetails?.id}-${id}`}>
                <UserIcon />
                <SidebarTopTextBold>
                  {` Applicant${applicants.length > 1 ? ` ${index + 1}` : ''}: `}
                </SidebarTopTextBold>

                <UserNameDiv ref={(el) => { textRefs.current[index] = el; }}>
                  {personalDetails?.firstName && personalDetails?.lastName
                    ? `${personalDetails.firstName} ${personalDetails.lastName}`
                    : '-'}
                </UserNameDiv>
              </SidebarTopText>
            </SidebarMenuItem>
            {isTruncated[index] && (
            <Tooltip
              id={`${personalDetails?.id}-${id}`}
              html={`${personalDetails?.firstName ?? ''} ${personalDetails?.lastName ?? ''}`}
              place="top"
              widthOverload={200}
            />
            )}
          </>
        ))}

        {isMsalAuth && (
          <>
            <SidebarMenuItem>
              <SidebarTopText id="sidebar-brokerage-name">
                <SidebarTopTextBold>
                  Brokerage name:
                </SidebarTopTextBold>
                <UserNameDiv ref={(el) => { textRefs.current[applicants?.length] = el; }}>
                  {account?.brokerageName || '-'}
                </UserNameDiv>
              </SidebarTopText>
            </SidebarMenuItem>
            {isTruncated[applicants.length] && (
              <Tooltip
                id="sidebar-brokerage-name"
                html={account?.brokerageName || ''}
                place="top"
                widthOverload={200}
              />
            )}
          </>
        )}
      </>
      )}

      <SidebarDivider />
    </>
  );
}
